import React, { useState } from 'react';
import { X, RefreshCw, Check, Hash, FileText, DollarSign, Landmark, Repeat2, ChevronsDown, AlertTriangle } from 'lucide-react';
import type { EditModeSettings } from '../../../../hooks/useEditModeSettings';

interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  settings: EditModeSettings;
  toggleColumn: (key: keyof EditModeSettings['columns']) => void;
  togglePreference: (key: keyof EditModeSettings['preferences']) => void;
  resetToDefaults: () => void;
}

type Tab = 'columns' | 'preferences';

export default function SettingsDrawer({
  open,
  onClose,
  settings,
  toggleColumn,
  togglePreference,
  resetToDefaults,
}: SettingsDrawerProps) {
  const [tab, setTab] = useState<Tab>('columns');

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        style={{ background: 'rgba(14,26,26,0.3)' }}
        onClick={onClose}
      />
      <div
        className="fixed top-0 right-0 bottom-0 w-full md:w-[440px] bg-white border-l border-line z-50 flex flex-col"
        style={{ boxShadow: '-10px 0 40px rgba(14,26,26,0.14)' }}
      >
        <div className="px-6 py-5 border-b border-line flex justify-between items-start gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="m-0 text-xl font-extrabold text-ink" style={{ letterSpacing: '-0.3px' }}>Settings</h2>
            <div className="text-[12.5px] text-ink3 mt-1 leading-snug">Customize what shows up on the Chart of accounts in Edit mode.</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close settings"
            className="w-9 h-9 rounded-md text-ink3 hover:bg-panel hover:text-ink flex items-center justify-center bg-transparent border border-transparent hover:border-line cursor-pointer flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex px-6 border-b border-line bg-white">
          <TabButton active={tab === 'columns'} onClick={() => setTab('columns')}>Columns</TabButton>
          <TabButton active={tab === 'preferences'} onClick={() => setTab('preferences')}>Preferences</TabButton>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3">
          {tab === 'columns' && (
            <>
              <ToggleRow icon={<Hash />} label="Account number" help="A numeric code next to each account (eg. 1010)." value={settings.columns.accountNumber} onChange={() => toggleColumn('accountNumber')} />
              <ToggleRow icon={<FileText />} label="Description" help="A short note shown below the account name." value={settings.columns.description} onChange={() => toggleColumn('description')} />
              <ToggleRow icon={<DollarSign />} label="Novala balance" help="Current ledger balance in Novala." value={settings.columns.novalaBalance} onChange={() => toggleColumn('novalaBalance')} />
              <ToggleRow icon={<Landmark />} label="Bank balance" help="Latest balance from the connected bank feed." value={settings.columns.bankBalance} onChange={() => toggleColumn('bankBalance')} />
            </>
          )}
          {tab === 'preferences' && (
            <>
              <ToggleRow icon={<Repeat2 />} label="Auto update sub account numbers" help="When you move an account under a parent, re-number its sub-accounts." value={settings.preferences.autoUpdateSubNumbers} onChange={() => togglePreference('autoUpdateSubNumbers')} />
              <ToggleRow icon={<ChevronsDown />} label="Collapse sections on load" help="Open Edit mode with every category collapsed." value={settings.preferences.collapseOnLoad} onChange={() => togglePreference('collapseOnLoad')} />
              <ToggleRow icon={<AlertTriangle />} label="Confirm before deleting" help="Ask for confirmation when you tap the trash icon." value={settings.preferences.confirmBeforeDelete} onChange={() => togglePreference('confirmBeforeDelete')} />
            </>
          )}
        </div>

        <div className="px-6 py-3.5 border-t border-line bg-white flex justify-between items-center gap-3">
          <button
            type="button"
            onClick={resetToDefaults}
            className="text-ink3 hover:text-ink text-[12.5px] font-semibold bg-transparent border-none p-0 cursor-pointer inline-flex items-center gap-1.5"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Reset
          </button>
          <span className="inline-flex items-center gap-1.5 text-[12px] font-medium" style={{ color: '#0F6E56' }}>
            <Check className="w-3.5 h-3.5" strokeWidth={2.5} /> All changes saved
          </span>
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-[18px] rounded-lg text-white font-semibold text-[13px] border-none cursor-pointer"
            style={{ background: '#0E1A1A' }}
          >
            Done
          </button>
        </div>
      </div>
    </>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-1 py-3 mr-6 text-[13px] font-semibold bg-transparent border-none cursor-pointer ${active ? 'text-ink' : 'text-ink3 hover:text-ink2'}`}
      style={{ borderBottom: active ? '2px solid #0F6E56' : '2px solid transparent', position: 'relative', top: 1 }}
    >
      {children}
    </button>
  );
}

function ToggleRow({ icon, label, help, value, onChange }: { icon: React.ReactNode; label: string; help: string; value: boolean; onChange: () => void }) {
  return (
    <div className="flex items-center gap-3.5 px-4 py-3.5 rounded-xl hover:bg-panel">
      <div className="w-5 h-5 text-ink flex-shrink-0 flex items-center justify-center">
        {React.isValidElement(icon)
          ? React.cloneElement(icon as React.ReactElement<any>, { className: 'w-[18px] h-[18px]' })
          : icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-semibold text-ink leading-tight">{label}</div>
        <div className="text-[12px] text-ink3 mt-1 leading-snug">{help}</div>
      </div>
      <button
        type="button"
        onClick={onChange}
        role="switch"
        aria-checked={value}
        aria-label={label}
        className="w-10 rounded-full relative flex-shrink-0 border-none p-0 cursor-pointer"
        style={{ height: 22, background: value ? '#0F6E56' : '#D8DEE6', transition: 'background 0.18s' }}
      >
        <span
          className="absolute top-0.5 bg-white rounded-full"
          style={{ left: value ? 20 : 2, width: 18, height: 18, transition: 'left 0.18s', boxShadow: '0 1px 3px rgba(14,26,26,0.2)' }}
        ></span>
      </button>
    </div>
  );
}